import React, { useRef } from 'react';
import styled from 'styled-components';
import { colors } from '../../style';

type Side = 'left' | 'right' | 'top' | 'bottom';

type ResizableDrawerProps = {
  children: React.ReactNode;
  /** Which edge the drawer is anchored to. Determines handle placement and resize axis. */
  side?: Side;
  /** Minimum size in pixels. The drawer cannot be resized smaller than this. */
  minSize?: number;
  /** Maximum size in pixels. The drawer cannot be resized larger than this. */
  maxSize?: number;
  /** Initial size in pixels. Defaults to minSize. */
  defaultSize?: number;
  /** Called on every resize with the new size in pixels. Fires during drag and on double-click toggle. */
  onSizeChange?: (size: number) => void;
  className?: string;
  style?: React.CSSProperties;
};

const isHorizontal = (side: Side) => side === 'left' || side === 'right';

const HANDLE_THICKNESS = 8;
const HANDLE_PILL_LENGTH = 32;
const HANDLE_PILL_THICKNESS = 4;

const handlePosition: Record<Side, string> = {
  right: 'left: 0; top: 0; bottom: 0;',
  left: 'right: 0; top: 0; bottom: 0;',
  bottom: 'top: 0; left: 0; right: 0;',
  top: 'bottom: 0; left: 0; right: 0;',
};

const Handle = styled.div<{ $side: Side }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  user-select: none;
  cursor: ${({ $side }) => (isHorizontal($side) ? 'col-resize' : 'row-resize')};
  ${({ $side }) => (isHorizontal($side) ? `width: ${HANDLE_THICKNESS}px;` : `height: ${HANDLE_THICKNESS}px;`)}
  ${({ $side }) => handlePosition[$side]}

  &::after {
    content: '';
    border-radius: 2px;
    background-color: ${colors.gray400};
    transition: background-color 150ms ease;
    width: ${({ $side }) => (isHorizontal($side) ? HANDLE_PILL_THICKNESS : HANDLE_PILL_LENGTH)}px;
    height: ${({ $side }) => (isHorizontal($side) ? HANDLE_PILL_LENGTH : HANDLE_PILL_THICKNESS)}px;
  }

  &:hover::after {
    background-color: ${colors.gray600};
  }
`;

const Container = styled.div<{ $side: Side; $size: number; $isTransitioning: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex-shrink: 0;
  ${({ $side, $size }) => `${isHorizontal($side) ? 'width' : 'height'}: ${$size}px;`}
  ${({ $isTransitioning, $side }) =>
    $isTransitioning ? `transition: ${isHorizontal($side) ? 'width' : 'height'} 200ms ease;` : ''}
`;

function ResizableDrawer({
  children,
  side = 'right',
  minSize = 200,
  maxSize = 500,
  defaultSize,
  onSizeChange,
  className,
  style,
}: ResizableDrawerProps) {
  const resolvedDefault = defaultSize ?? minSize;
  const [size, setSize] = React.useState(resolvedDefault);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const isDragging = useRef(false);
  const startPos = useRef(0);
  const startSize = useRef(0);

  const clamp = (value: number) => Math.min(maxSize, Math.max(minSize, value));

  const updateSize = (newSize: number) => {
    const clamped = clamp(newSize);
    setSize(clamped);
    onSizeChange?.(clamped);
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    isDragging.current = true;
    startPos.current = isHorizontal(side) ? event.clientX : event.clientY;
    startSize.current = size;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!isDragging.current) {
      return;
    }
    const currentPos = isHorizontal(side) ? event.clientX : event.clientY;
    const delta = currentPos - startPos.current;
    const direction = side === 'right' || side === 'bottom' ? -1 : 1;
    updateSize(startSize.current + delta * direction);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  const handleDoubleClick = () => {
    setIsTransitioning(true);
    const target = size > minSize ? minSize : (defaultSize ?? maxSize);
    updateSize(target);
  };

  return (
    <Container
      $side={side}
      $size={size}
      $isTransitioning={isTransitioning}
      className={className}
      style={style}
      onTransitionEnd={() => setIsTransitioning(false)}
    >
      <Handle
        $side={side}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      />
      {children}
    </Container>
  );
}

export { ResizableDrawer, type ResizableDrawerProps };
